import { Card, Popover, Tooltip } from "@mui/material";
import { cn } from "components/lib/utils";
import UserAutoComplete from "components/UserAutocomplete";
import { SectionValueView, UserInfo } from "generated/graphql";
import { usePopover } from "helpers/hooks/usePopover";
import { fullName, nameInitials } from "helpers/name-helpers";
import { HTMLAttributes } from "react";
import { useAppSelector } from "redux/hooks";
import { selectClientLensAssignees } from "redux/slices/clientLens";
import { Add } from "@mui/icons-material";
import { useSectionValue } from "./useSectionValue";
import { selectMyUserId } from "redux/slices/session";
import { selectUsers } from "redux/slices/client";
import { isNotNull } from "Backend";
import { stripNull } from "helpers/coll";

export function AssigneeChip({
  isMe,
  user,
  tooltipRole,
  className,
  deEmphasize,
  onClick,
}: {
  isMe: boolean;
  tooltipRole: { isQa: boolean } | "full-name";
  user?: UserInfo;
  deEmphasize?: boolean;
} & HTMLAttributes<HTMLDivElement>) {
  const chipColor = deEmphasize
    ? "bg-gray-100 border-gray-200"
    : isMe
      ? "bg-peri-600 border-peri-800"
      : user !== undefined
        ? "bg-gray-400 border-gray-500"
        : "border-gray-200 bg-white";
  return (
    <Tooltip
      title={
        user !== undefined
          ? `${tooltipRole === "full-name" ? fullName(user) : tooltipRole.isQa ? "QA Assignee" : "Review Assignee"}: ${user.email}`
          : undefined
      }>
      <div
        onClick={onClick}
        className={cn(
          "rounded-full size-6 flex items-center justify-center p-2 cursor-pointer border",
          chipColor,
          className
        )}>
        {user !== undefined ? (
          <p className="text-xxs font-bold text-white">{nameInitials(user)}</p>
        ) : (
          <Add className="text-gray-200" />
        )}
      </div>
    </Tooltip>
  );
}

export function AssigneeChipWithPopover({
  user,
  isMe,
  isQa,
  caseId,
  sectionId,
  formId,
  className,
}: {
  user?: UserInfo;
  isMe: boolean;
  isQa: boolean;
  caseId: number;
  formId: number;
  sectionId: number;
} & HTMLAttributes<HTMLDivElement>) {
  const popover = usePopover();
  const clientLensAssignees = useAppSelector(selectClientLensAssignees);
  const namedUser = clientLensAssignees.find((a) => a.id === user?.id);
  const sectionValueHook = useSectionValue({ caseId, formId });

  return (
    <>
      <AssigneeChip
        isMe={isMe}
        tooltipRole={{ isQa }}
        user={user}
        className={className}
        onClick={(evt) => {
          evt.stopPropagation();
          popover.onPopoverOpen(evt.currentTarget);
        }}
      />
      <Popover open={popover.isOpen} anchorEl={popover.anchorEl} onClose={popover.onPopoverClose}>
        <Card className="p-4 pb-6 min-w-80">
          <UserAutoComplete
            label="Search"
            variant="standard"
            textFieldProps={{
              autoFocus: true,
            }}
            options={clientLensAssignees}
            selected={namedUser}
            onChange={(newAssignee) => {
              isQa
                ? sectionValueHook.updateValidationAssignee(sectionId, newAssignee?.id)
                : sectionValueHook.updateOutcomeAssignee(sectionId, newAssignee?.id);
              if (newAssignee !== undefined) {
                popover.onPopoverClose();
              }
            }}
          />
        </Card>
      </Popover>
    </>
  );
}

export function StackedAssigneeChips({
  qaSections,
  reviewSections,
  completeSections,
  deEmphasize,
}: {
  qaSections: SectionValueView[];
  reviewSections: SectionValueView[];
  completeSections: SectionValueView[];
  deEmphasize: boolean;
}) {
  const myUserId = useAppSelector(selectMyUserId);
  const users = useAppSelector(selectUsers);
  const completeSectionsAssignees = new Set(
    completeSections
      .map((sv) => [sv.validationAssignee, sv.outcomeAssignee])
      .flat()
      .filter(isNotNull)
  );
  const qaSectionsAssignees = qaSections.map((sv) => sv.validationAssignee);
  const reviewSectionsAssignees = reviewSections.map((sv) => sv.outcomeAssignee);
  const incompleteSectionAssignees = new Set(
    qaSectionsAssignees.map(stripNull).concat(reviewSectionsAssignees.map(stripNull))
  );

  const assignees = Array.from(
    new Set(Array.from(completeSectionsAssignees).concat(Array.from(incompleteSectionAssignees)))
  );
  const chipAssignees = assignees.slice(0, 3);
  const additionalAssignees = assignees.length - chipAssignees.length;

  return (
    <div className="flex gap-1 items-center">
      <div className="grid grid-cols-1">
        {chipAssignees.map((assigneeId, index) => (
          <AssigneeChip
            deEmphasize={
              deEmphasize || (completeSectionsAssignees.has(assigneeId) && !incompleteSectionAssignees.has(assigneeId))
            }
            className={cn(
              "col-start-1 row-start-1",
              // First chip starts at the left, remaining chips are pushed to the right
              index > 0 ? (index === 1 ? " ml-3" : index === 2 ? " ml-6" : " ml-9") : ""
            )}
            key={assigneeId}
            isMe={assigneeId === myUserId}
            tooltipRole="full-name"
            user={users.find((u) => u.id === assigneeId)}
          />
        ))}
      </div>
      {additionalAssignees > 0 && <p className="text-xs">+{additionalAssignees}</p>}
    </div>
  );
}
